import React from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { Feedback, InputField, InputFieldProps } from '@placer-ui/components';

type InputFieldControllerProps<T extends FieldValues = FieldValues> = Omit<
    InputFieldProps,
    'onChange'
> &
    Required<Pick<UseControllerProps<T>, 'control'>> &
    UseControllerProps<T> &
    { testId?: string };

export const InputFieldController = <T extends FieldValues = FieldValues>(
    props: InputFieldControllerProps<T>,
) => {
    const { control, rules, name, shouldUnregister, defaultValue, disabled, ...inputProps } = props;

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            disabled={disabled}
            render={({ field: { onChange, value, ...restField }, formState }) => {
                const errorMessage = formState.errors[name]?.message as string;
                const errorType = formState.errors[name]?.type as string;
                const isError = Boolean(errorMessage || errorType);

                const handleChange = (val: string) => {
                    onChange(val);
                };

                const isEmailInput = ['username', 'email'].includes(name);
                const feedback: Feedback[] | undefined =
                    // only Email type input field has invalid feedback message
                    isEmailInput && isError
                        ? [
                              {
                                  message: errorMessage,
                                  state: 'error',
                                  hideIcon: true,
                              },
                          ]
                        : undefined;

                return (
                    <InputField
                        {...restField}
                        {...inputProps}
                        onChange={handleChange}
                        isError={isError}
                        value={value || ''}
                        required={errorType === 'required'}
                        feedback={feedback}
                    />
                );
            }}
        />
    );
};
